
import { defineComponent, ref, reactive, computed, watch } from 'vue'
import { FieldsStructure, FieldStructure } from '@/contracts/forms/FieldContracts'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { orders } from '@/composables/orders'
import Form from '@/components/Form.vue'
import { pagination } from '@/composables/pagination'
import { prepareOrderTableColumns, getFormattedColumnValue } from '@/composables/table'
import { NuxeoPageProviderResult, NuxeoDocumentsDownloadedResponse, NuxeoPageProviderResponse, HMCTSOrderState } from '@/contracts/api/ResponseContracts'
import { axiosDownload } from '@/composables/api'
import { nuxeoDocumentsDownloaded, nuxeoCustomerOrders } from '@/composables/nuxeo'
import download from 'downloadjs'
import Tabs from '@/components/Tabs.vue'

export default defineComponent(
{
    name: 'Orders',
    components:
    {
        Tabs,
        Form
    },
    setup()
    {
        // Load router:
        const router = useRouter()
        // Use vuex:
        const store = useStore()
        // Get form data:
        const stage1Data = store.getters['formData/stage1Data']
        const stage2Data = store.getters['formData/stage2Data']
        const stageData = reactive(Object.assign(stage1Data, stage2Data))
        // Hold visible row:
        const visibleRowUid = ref('')
        // Get user data:
        const authenticatedUser = computed(() =>
        {
            return store.getters['auth/getUser']
        })
        // Set columns to display based on grant type:
        const columns = prepareOrderTableColumns()
        // Prepare for orders:
        const {
            data,
            numberOfPaginationPages,
            currentPageIndex,
            sortDirection,
            sortColumn,
            getOrders,
            prevPage,
            nextPage,
            goToPage,
            setFilterParams
        } = orders()
        // Setup pagination:
        const {
            currentPageNumber,
            numberOfResults,
            numberOfResultsText,
            isFirstPage,
            isLastPage,
            paginationPages
        } = pagination(currentPageIndex, numberOfPaginationPages, data, stageData.grantType)
        // Setup tabs:
        const tabs =
        [
            {
                id: 'active-orders',
                label: 'Active Orders'
            },
            {
                id: 'expired-orders',
                label: 'Expired Orders'
            }
        ]

        // Hold customer order numbers:
        const customerOrderNumbers = ref([] as Array<string>)
        // Get customer order numbers:
        const getCustomerOrderNumbers = function()
        {
            // Reset customer order numbers:
            customerOrderNumbers.value = []
            // Fetch numbers:
            nuxeoCustomerOrders(
            authenticatedUser.value.id,
            authenticatedUser.value.password,
            function(response: NuxeoPageProviderResponse)
            {
                for(let i = 0; i < response.entries.length; i++)
                {
                    if(response.entries[i].properties['hmctsorder:orderNumber'] !== undefined)
                    {
                        customerOrderNumbers.value.push(String(response.entries[i].properties['hmctsorder:orderNumber']))
                    }
                }
            })
        }

        // Split entries into active and pending:
        const activeOrders = reactive([] as Array<NuxeoPageProviderResult>)
        const expiredOrders = reactive([] as Array<NuxeoPageProviderResult>)
        // Watch data change:
        watch(data, function(newData)
        {
            // Reset orders arrays:
            activeOrders.splice(0, activeOrders.length)
            expiredOrders.splice(0, expiredOrders.length)
            // Loop entries and set into correct array:
            for(let i = 0; i < newData.entries.length; i++)
            {
                const entry = newData.entries[i]
                if(documentsExpired(entry))
                {
                    expiredOrders.push(entry)
                }
                else
                {
                    activeOrders.push(entry)
                }
            }
            // Get customer order numbers:
            getCustomerOrderNumbers()
        })

        // Handle ordering of columns:
        const sortStatusMessage = ref('')
        const sortBy = function(column: string, direction: string, friendlyName: string)
        {
            // Set new sort column:
            sortColumn.value = column
            // Set sort direction:
            if(sortDirection.value === 'none' || sortDirection.value === 'descending')
            {
                sortDirection.value = 'ascending'
            }
            else
            {
                sortDirection.value = 'descending'
            }
            // Set status message for screen readers:
            sortStatusMessage.value = 'Sort by ' + friendlyName + ' (' + sortDirection.value + ')'
            // Trigger search:
            getOrders()
        }

        // Determine if field is a year:
        const isYearField = function(field: FieldStructure): boolean
        {
            if(field.rules.year !== undefined)
            {
                return true
            }
            return false
        }

        // Check if documents exist:
        const documentsCanBeDownloaded = function(entry: NuxeoPageProviderResult)
        {
            return ((entry.properties['file:content']) || (entry.properties['hmctsgrant:willdoc']))
        }
        const grantCanBeDownloaded = function(entry: NuxeoPageProviderResult)
        {
            return entry.properties['file:content']
        }
        const willCanBeDownloaded = function(entry: NuxeoPageProviderResult)
        {
            return entry.properties['hmctsgrant:willdoc']
        }
        // Check if documents have expired:
        const documentsExpired = function(entry: NuxeoPageProviderResult): boolean
        {
            const itemAvailableUntilDate = getFromOrderState(entry, 'itemAvailableUntilDate')
            if(itemAvailableUntilDate === null)
            {
                return false
            }
            return (new Date()) > (new Date(itemAvailableUntilDate))
        }

        // Download documents:
        const downloadDocuments = function(entry: NuxeoPageProviderResult, type: string): void
        {
            const grantDoc = entry.properties['file:content']
            const willDoc = entry.properties['hmctsgrant:willdoc']
            if(grantDoc !== undefined && type === 'grant')
            {
                const url = grantDoc.data
                const grantResponse = axiosDownload(url.split('nuxeo')[1])
                watch(grantResponse, (response: any) =>
                {
                    console.log('watch grant')
                    download(response, grantDoc.name, grantDoc['mime-type'])
                })
            }
            if(willDoc !== undefined && type === 'will')
            {
                const url = willDoc.data
                const willResponse = axiosDownload(url.split('nuxeo')[1])
                watch(willResponse, (response: any) =>
                {
                    console.log('watch will')
                    download(response, willDoc.name, willDoc['mime-type'])
                })
            }
            if(documentsCanBeDownloaded(entry))
            {
                const collectionID = getFromOrderState(entry, 'collectionID')
                nuxeoDocumentsDownloaded(
                authenticatedUser.value.id,
                authenticatedUser.value.password,
                {
                    collectionID: String(collectionID)
                },
                entry.uid,
                function(response: NuxeoDocumentsDownloadedResponse)
                {
                    console.log(response)
                })
            }
        }

        // Get a value from order state:
        const getFromOrderState = function(entry: NuxeoPageProviderResult, value: keyof HMCTSOrderState): string | number
        {
            for(let i = 0; i < entry.properties['hmctsgrant:ordersState'].length; i++)
            {
                const orderStateItem = entry.properties['hmctsgrant:ordersState'][i]
                if(customerOrderNumbers.value.includes(orderStateItem.orderNumber))
                {
                    return orderStateItem[value]
                }
            }
            return ''
        }

        // Generate href for order contact:
        const getContactOrderHref = function(entry: NuxeoPageProviderResult): string
        {
            const orderNumber = getFromOrderState(entry, 'orderNumber')
            if(orderNumber === null || orderNumber === undefined)
            {
                return ''
            }
            return 'mailto:HMCTSWills@ironmountain.co.uk?subject=Order Number: ' + orderNumber
        }

        // Function to check if hidden rows exist:
        const hasHiddenRowsExist = function(entry: NuxeoPageProviderResult): boolean
        {
            // Count rows:
            let rowCount = 0
            // Check how many rows exist:
            for(let i = 0; i < entry.properties['hmctsgrant:ordersState'].length; i++)
            {
                if(customerOrderNumbers.value.includes(entry.properties['hmctsgrant:ordersState'][i].orderNumber))
                {
                    rowCount++
                }
            }
            // Check rows:
            if(rowCount > 1)
            {
                return true
            }
            else
            {
                return false
            }
        }

        // Show hidden rows for passed entry:
        const showHiddenRows = function(entry: NuxeoPageProviderResult)
        {
            if(entry.uid === visibleRowUid.value)
            {
                 visibleRowUid.value = ''
            }
            else
            {
                if(hasHiddenRowsExist(entry))
                {
                    visibleRowUid.value = entry.uid
                }
                else
                {
                    visibleRowUid.value = ''
                }
            }
        }

        // Class list for hidden rows
        const getParentHiddenRowClassList = function(entry: NuxeoPageProviderResult): string
        {
            if(hasHiddenRowsExist(entry))
            {
                return 'govuk-table__row has-hidden-rows'
            }
            return 'govuk-table__row'
        }

        // Class list for hidden rows
        const getHiddenRowClassList = function(uid: string): string
        {
            if(uid === visibleRowUid.value)
            {
                return 'govuk-table__row hidden-row'
            }
            return 'govuk-table__row hidden-row hidden'
        }

        // Set fieldsets for filter fields:
        const fieldsets =
        [
            {
                name: 'ordernumber',
                text: 'Order number'
            },
            {
                name: 'surname',
                text: 'Surname'
            },
            {
                name: 'firstnames',
                text: 'First names'
            }
        ]
        // Set fields for filtering:
        const fields =
        {
            ordernumber:
            {
                name: 'ordernumber',
                type: 'Input',
                label: 'Order number',
                inputType: 'text',
                fieldset: 'ordernumber',
                width: 'govuk-input--width-20'
            },
            surname:
            {
                name: 'surname',
                type: 'Input',
                label: 'Surname',
                inputType: 'text',
                fieldset: 'surname',
                width: 'govuk-input--width-20'
            },
            firstnames:
            {
                name: 'firstnames',
                type: 'Input',
                label: 'First names',
                inputType: 'text',
                fieldset: 'firstnames',
                width: 'govuk-input--width-20'
            }
        }

        // Set typing timer:
        let typingTimer: ReturnType<typeof setTimeout>
        const typingInterval = 1000
        // Listen for filter field changes:
        const filterOrders = function(filterData: FieldsStructure)
        {
            clearTimeout(typingTimer)
            typingTimer = setTimeout(() => setFilterParams(JSON.parse(JSON.stringify(filterData))), typingInterval)
        }

        // Set hover index for contact help message:
        const hoverMessageIndex = ref(null)

        // const onHover = function(index: number)
        // {
        //     hoverMessageIndex.value = index
        //     console.log('WHHAH', hoverMessageIndex)
        // }

        // Return data:
        return {
            stageData,
            columns,
            data,
            activeOrders,
            expiredOrders,
            currentPageNumber,
            numberOfResults,
            isFirstPage,
            isLastPage,
            paginationPages,
            prevPage,
            nextPage,
            goToPage,
            tabs,
            sortDirection,
            sortColumn,
            sortStatusMessage,
            sortBy,
            isYearField,
            documentsCanBeDownloaded,
            documentsExpired,
            downloadDocuments,
            grantCanBeDownloaded,
            willCanBeDownloaded,
            getFormattedColumnValue,
            getContactOrderHref,
            getFromOrderState,
            visibleRowUid,
            showHiddenRows,
            getParentHiddenRowClassList,
            getHiddenRowClassList,
            customerOrderNumbers,
            fieldsets,
            fields,
            filterOrders,
            hoverMessageIndex
        }
    }
})
